import { useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import PhotoSlot from './PhotoSlots';
import { MX4_COACHES, MX4_COACHES_PICTURE_SOON, MX4_SUBS } from '../data/mx4Coaches';
import { BRAND_ASSETS } from '../lib/mediaTruth';
import './MX4CoachProfiles.css';

const PERSONAL_LABELS = [
  ['offTheFloor', 'Off the floor'],
  ['signatureMove', 'Signature move'],
  ['soundtrack', 'Class soundtrack'],
];
const SUBJECT = { her: 'she', him: 'he' };
const POSSESSIVE = { her: 'her', him: 'his' };
const hasProfile = (coach) => coach.profile !== false;
const toneClass = (coach) => `dv-mx4-tone--${coach.tone || 'red'}`;

// Line illustrations (decorative only): an icon for each coaching-style word, an interval pulse
// line and a large crossed mark that sits behind each open profile.
const ICONS = {
  spark: <path d="M12 2v5M12 17v5M2 12h5M17 12h5M5 5l3.5 3.5M15.5 15.5L19 19M19 5l-3.5 3.5M8.5 15.5L5 19" />,
  sun: <><circle cx="12" cy="12" r="4.5" /><path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3M4.6 4.6l2.1 2.1M17.3 17.3l2.1 2.1M19.4 4.6l-2.1 2.1M6.7 17.3l-2.1 2.1" /></>,
  heart: <path d="M12 20.5S3.5 15.2 3.5 9.1A4.6 4.6 0 0 1 12 6.6a4.6 4.6 0 0 1 8.5 2.5c0 6.1-8.5 11.4-8.5 11.4Z" />,
  shield: <path d="M12 2.5 4 5.5v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10v-6l-8-3Z" />,
  target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.2" /></>,
  hourglass: <path d="M6 2.5h12M6 21.5h12M7 2.5c0 5 10 5 10 9.5S7 16.5 7 21.5M17 2.5c0 5-10 5-10 9.5s10 4.5 10 9.5" />,
};
const TRAIT_ICONS = {
  positive: 'spark', empowering: 'spark', driven: 'spark',
  happy: 'sun', upbeat: 'sun',
  grateful: 'heart', joyful: 'heart', invested: 'heart', 'whole-person': 'heart',
  safe: 'shield',
  disciplined: 'target', dedicated: 'target', 'goal-driven': 'target',
  patient: 'hourglass', experienced: 'hourglass',
};
const TraitIcon = ({ trait }) => (
  <svg className="dv-mx4-coach__trait-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {ICONS[TRAIT_ICONS[trait.toLowerCase()] || 'spark']}
  </svg>
);
const PulseLine = () => (
  <svg className="dv-mx4-coach__pulse" viewBox="0 0 240 24" preserveAspectRatio="none" aria-hidden="true" focusable="false">
    <path pathLength="1" d="M0 12h58l6-9 8 18 7-14 5 5h40l6-9 8 18 7-14 5 5h82" />
  </svg>
);
const CrossMark = ({ className }) => (
  <svg className={className} viewBox="0 0 200 200" aria-hidden="true" focusable="false">
    <path d="M20 20 180 180M180 20 20 180" />
    <path d="M48 20 180 152M152 20 20 152" />
  </svg>
);

// Portrait with film texture and a colored haze that dissolves into the name and bio below it.
export function CoachPortrait({ coach, sizes, className = '' }) {
  return (
    <span className={`dv-mx4-portrait ${className}`.trim()}>
      <PhotoSlot id={`coach-${coach.slug}`} label={`${coach.name} portrait`} sizes={sizes} className="dv-mx4-portrait__photo" />
      <span className="dv-mx4-portrait__texture" aria-hidden="true" />
    </span>
  );
}

// The full profile, shared by the on-page cards and the full-screen coaches view.
function CoachDetails({ coach, onJump }) {
  const personal = PERSONAL_LABELS.filter(([key]) => coach.personal?.[key]);
  const jump = (href) => (onJump ? (event) => { event.preventDefault(); onJump(href); } : undefined);
  return (
    <>
      <CrossMark className="dv-mx4-coach__mark" />
      <div className="dv-mx4-coach__story">
        <p className="dv-eyebrow">Your first session with {coach.name}</p>
        <PulseLine />
        <p className="dv-size-lede">{coach.firstSession}</p>
        {coach.quote && (
          <blockquote className="dv-mx4-coach__quote">
            <p className="dv-size-body">“{coach.quote}”</p>
            <cite>{coach.name}, in {POSSESSIVE[coach.pronoun] || 'their'} own words</cite>
          </blockquote>
        )}
      </div>

      <div className="dv-mx4-coach__details">
        <div>
          <p className="dv-eyebrow">Coaching style</p>
          <dl className="dv-mx4-coach__style">
            {coach.style.map(([trait, detail]) => (
              <div key={trait}><dt><TraitIcon trait={trait} />{trait}</dt><dd className="dv-size-body">{detail}</dd></div>
            ))}
          </dl>
        </div>
        <div>
          <p className="dv-eyebrow">Best for</p>
          <ul className="dv-mx4-coach__list">
            {coach.bestFor.map((item) => <li className="dv-size-body" key={item}>{item}</li>)}
          </ul>
        </div>
        {personal.length > 0 && (
          <div>
            <p className="dv-eyebrow">Get to know {coach.name}</p>
            <dl className="dv-mx4-coach__style">
              {personal.map(([key, label]) => (
                <div key={key}><dt>{label}</dt><dd className="dv-size-body">{coach.personal[key]}</dd></div>
              ))}
            </dl>
          </div>
        )}
        <div>
          <p className="dv-eyebrow">What {SUBJECT[coach.pronoun] || 'they'} brings</p>
          <ul className="dv-mx4-coach__highlights">
            {coach.highlights.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="dv-mx4-coach__label">Teaches</p>
          <ul className="dv-mx4-coach__chips">
            {coach.classes.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="dv-mx4-coach__label">Certifications</p>
          <ul className="dv-mx4-coach__certs">
            {coach.certifications.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <div className="dv-service-actions dv-mx4-coach__actions">
          <a className="dv-button dv-button--light" href="#mx4-inquiry" onClick={jump('#mx4-inquiry')}>Train with {coach.name}</a>
          <a className="dv-button dv-button--outline-light" href="#mx4-schedule" onClick={jump('#mx4-schedule')}>See the Schedule</a>
        </div>
      </div>
    </>
  );
}

// Coaches whose pictures are still coming, then substitute coaches, listed under the cards.
function MoreCoaches() {
  return (
    <div className="dv-mx4-coaches__more">
      <ul className="dv-mx4-coaches__soon" aria-label="More MX4 coaches">
        {MX4_COACHES_PICTURE_SOON.map((name) => (
          <li key={name} className="dv-mx4-coaches__soon-card">
            <span className="dv-mx4-coaches__soon-photo">Picture coming soon</span>
            <span className="dv-size-subheading dv-mx4-coach__name">{name}</span>
            <span className="dv-mx4-coach__title">MX4 Coach</span>
          </li>
        ))}
      </ul>
      <div className="dv-mx4-coaches__subs">
        <p className="dv-eyebrow">Substitute coaches</p>
        <ul>{MX4_SUBS.map((name) => <li key={name} className="dv-size-subheading">{name}</li>)}</ul>
      </div>
    </div>
  );
}

// Coach cards expand into a full profile (one open at a time) so visitors can get to know the
// person who will coach their first MX4 session.
export default function MX4CoachProfiles({ coaches = MX4_COACHES, onOpenShowcase }) {
  const [open, setOpen] = useState(null);

  return (
    <section id="mx4-coaches" className="dv-mx4-coaches" aria-labelledby="mx4-coaches-title">
      <div className="dv-mx4-coaches__heading">
        <p className="dv-eyebrow">Meet your MX4 coaches</p>
        <h2 className="dv-size-heading" id="mx4-coaches-title">
          <span>Know your coach</span>
          <em>before your first class.</em>
        </h2>
        <p className="dv-size-lede">Tap a coach to see how they coach, who they work best with, and what your first session with them feels like.</p>
        {onOpenShowcase && (
          <button type="button" className="dv-button dv-button--outline-light dv-mx4-coaches__showcase" onClick={onOpenShowcase}>Full-Screen Coach View</button>
        )}
      </div>

      <div className="dv-mx4-coaches__grid">
        {coaches.map((coach) => {
          if (!hasProfile(coach)) {
            return (
              <article key={coach.slug} className={`dv-mx4-coach ${toneClass(coach)}`} data-testid={`mx4-coach-${coach.slug}`}>
                <div className="dv-mx4-coach__card dv-mx4-coach__card--static">
                  <CoachPortrait coach={coach} sizes="(max-width: 640px) 100vw, 352px" />
                  <span className="dv-mx4-coach__summary">
                    <span className="dv-size-subheading dv-mx4-coach__name">{coach.name}</span>
                    <span className="dv-mx4-coach__title">{coach.title}</span>
                    <span className="dv-mx4-coach__toggle dv-mx4-coach__toggle--soon">Profile coming soon</span>
                  </span>
                </div>
              </article>
            );
          }
          const expanded = open === coach.slug;
          const panelId = `mx4-coach-${coach.slug}-profile`;
          return (
            <article key={coach.slug} className={`dv-mx4-coach ${toneClass(coach)}${expanded ? ' dv-mx4-coach--open' : ''}`} data-testid={`mx4-coach-${coach.slug}`}>
              <button type="button" className="dv-mx4-coach__card" aria-expanded={expanded} aria-controls={panelId}
                onClick={() => setOpen(expanded ? null : coach.slug)}>
                <CoachPortrait coach={coach} sizes="(max-width: 640px) 100vw, 352px" />
                <span className="dv-mx4-coach__summary">
                  <span className="dv-size-subheading dv-mx4-coach__name">{coach.name}</span>
                  <span className="dv-mx4-coach__title">{coach.title}</span>
                  <span className="dv-size-body dv-mx4-coach__hook">{coach.hook}</span>
                  <span className="dv-mx4-coach__toggle">{expanded ? `Close ${coach.name}’s profile` : `Get to know ${coach.name}`}</span>
                </span>
              </button>

              <div id={panelId} className="dv-mx4-coach__profile" role="region" aria-label={`${coach.name}’s profile`} hidden={!expanded}>
                <CoachDetails coach={coach} />
              </div>
            </article>
          );
        })}
      </div>

      <MoreCoaches />
    </section>
  );
}

// Full-screen coaches view opened from the MX4 header. The header band's media layer is where a
// background video can go later; for now it carries the red haze and texture.
export function MX4CoachShowcase({ open, onOpenChange, coaches = MX4_COACHES }) {
  const [active, setActive] = useState(coaches[0]?.slug);
  const pendingJump = useRef(null);
  const coach = coaches.find((item) => item.slug === active) || coaches[0];

  const jumpTo = (href) => {
    pendingJump.current = href;
    onOpenChange(false);
  };
  const jump = (href) => (event) => { event.preventDefault(); jumpTo(href); };
  const afterClose = (event) => {
    if (!pendingJump.current) return;
    event.preventDefault();
    document.querySelector(pendingJump.current)?.scrollIntoView?.();
    pendingJump.current = null;
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dv-mx4-showcase__overlay" />
        <DialogPrimitive.Content className="dv-mx4-showcase" data-testid="mx4-coach-showcase" aria-describedby={undefined} onCloseAutoFocus={afterClose}>
          <header className="dv-mx4-showcase__band">
            <span className="dv-mx4-showcase__media" aria-hidden="true" />
            <div className="dv-mx4-showcase__bar">
              <p className="dv-eyebrow">Delta Valley Health Club</p>
              <DialogPrimitive.Close className="dv-button dv-button--outline-light dv-mx4-showcase__close">Close</DialogPrimitive.Close>
            </div>
            <div className="dv-mx4-showcase__brand">
              <img src={BRAND_ASSETS.mx4Logo.src} alt={BRAND_ASSETS.mx4Logo.alt} data-brand-asset={BRAND_ASSETS.mx4Logo.id} data-brand-status={BRAND_ASSETS.mx4Logo.status} />
            </div>
            <div className="dv-mx4-showcase__intro">
              <DialogPrimitive.Title asChild>
                <h2 className="dv-size-heading"><span>Meet your coaches</span><em>before you ever walk in.</em></h2>
              </DialogPrimitive.Title>
              <p className="dv-size-lede">The people who will learn your name, set your pace and cheer the last interval.</p>
            </div>
            {coaches.length > 1 && (
              <nav className="dv-mx4-showcase__roster" aria-label="MX4 coaches">
                {coaches.map((item) => (
                  <button type="button" key={item.slug} aria-pressed={item.slug === coach.slug} onClick={() => setActive(item.slug)}>{item.name}</button>
                ))}
              </nav>
            )}
          </header>

          {coach && (
            <article className={`dv-mx4-showcase__stage ${toneClass(coach)}`} key={coach.slug} aria-label={`${coach.name}’s profile`}>
              <div className="dv-mx4-showcase__person">
                <CoachPortrait coach={coach} sizes="(max-width: 900px) 100vw, 480px" />
                <div className="dv-mx4-showcase__nameplate">
                  <h3 className="dv-size-subheading dv-mx4-coach__name">{coach.name}</h3>
                  <p className="dv-mx4-coach__title">{coach.title}</p>
                  {coach.hook && <p className="dv-size-body dv-mx4-coach__hook">{coach.hook}</p>}
                </div>
              </div>
              <div className="dv-mx4-coach__profile dv-mx4-showcase__profile">
                {hasProfile(coach) ? <CoachDetails coach={coach} onJump={jumpTo} /> : (
                  <div className="dv-mx4-showcase__soon">
                    <p className="dv-eyebrow">Profile coming soon</p>
                    <PulseLine />
                    <p className="dv-size-lede">{coach.name}’s full profile is on its way. Until then, come meet {coach.pronoun} in class.</p>
                    <div className="dv-service-actions dv-mx4-coach__actions">
                      <a className="dv-button dv-button--outline-light" href="#mx4-schedule" onClick={jump('#mx4-schedule')}>See the Schedule</a>
                    </div>
                  </div>
                )}
              </div>
            </article>
          )}

          <footer className="dv-mx4-showcase__more">
            <p><span className="dv-eyebrow">Pictures coming soon</span> {MX4_COACHES_PICTURE_SOON.join(' · ')}</p>
            <p><span className="dv-eyebrow">Substitute coaches</span> {MX4_SUBS.join(' · ')}</p>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
